import { useState } from "react";

import { isAPIError } from "@/core/services";
import { useAsyncList, useDialogForm, useToast } from "@/ds";
import { criarNovaVersao, getProximaVersao } from "@/modules/financeiro-configuracoes";
import type { ProximaVersao } from "@/modules/financeiro-configuracoes";
import { useChaveIdempotencia, useConfirmarDescarte } from "@/shared/hooks";
import {
  ESCOPOS_PADRAO_ERROS,
  ESCOPOS_PADRAO_ROTULOS,
  ESCOPOS_PADRAO_TOASTS,
  rotuloVersao,
} from "../../escopos-padrao.constants";
import {
  COPIADO_COM_CORRECAO,
  COPIADO_CUSTOS,
  COPIADO_ESCOPOS_CLASSE_OUTROS,
  COPIADO_ESCOPOS_EQUIPAMENTO,
  COPIADO_SEM_CORRECAO,
  COPIADO_TITULO,
  ESCOPOS_A_COPIAR,
  ETAPA_CONFIRMACAO,
  ETAPA_CORRECAO,
  INTRODUCAO,
  MS11,
  PERCENTUAL_PADRAO,
} from "./nova-versao-dialog.constants";
import {
  descreverCorrecao,
  lerPercentual,
  montarCorpo,
  percentualPreenchido,
} from "./nova-versao-dialog.rules";
import type {
  EtapaNovaVersao,
  NovaVersaoDialogProps,
  NovaVersaoDialogViewModel,
  NovaVersaoEstado,
} from "./types-nova-versao-dialog";

const SEM_PARAMETROS = {};
const VAZIA: ProximaVersao = { origem: "", destino: "", escoposACopiar: 0 };

export function useNovaVersaoDialog({
  onClose,
  onConcluido,
}: NovaVersaoDialogProps): NovaVersaoDialogViewModel {
  const toast = useToast();
  const { comChave } = useChaveIdempotencia();
  const [etapa, setEtapa] = useState<1 | 2>(1);
  const [avisoConflito, setAvisoConflito] = useState<string | null>(null);

  const proxima = useAsyncList<object, ProximaVersao>({
    fetcher: async (_params, signal) => ({ items: [await getProximaVersao({ signal })], total: 1 }),
    params: SEM_PARAMETROS,
    forbiddenMessage: ESCOPOS_PADRAO_ERROS.proximaVersaoSemPermissao,
    errorMessage: ESCOPOS_PADRAO_ERROS.proximaVersao,
    resolveErrorMessage: (erro) => (isAPIError(erro) ? erro.message : undefined),
  });
  const { origem: origemApi, destino: destinoApi, escoposACopiar } = proxima.items[0] ?? VAZIA;
  const origem = rotuloVersao(origemApi);
  const destino = rotuloVersao(destinoApi);

  const form = useDialogForm<{ servicos: string; materiais: string }>({
    initial: { servicos: PERCENTUAL_PADRAO, materiais: PERCENTUAL_PADRAO },
    validate: ({ servicos, materiais }) => ({
      servicos: lerPercentual(servicos).erro ?? undefined,
      materiais: lerPercentual(materiais).erro ?? undefined,
    }),
    onSubmit: async ({ servicos, materiais }) => {
      const corpo = montarCorpo(
        origemApi,
        lerPercentual(servicos).valor,
        lerPercentual(materiais).valor,
      );
      setAvisoConflito(null);
      try {
        const criada = await comChave(corpo, (idempotencyKey) =>
          criarNovaVersao(corpo, { idempotencyKey }),
        );
        toast.success(ESCOPOS_PADRAO_TOASTS.versaoCriada(criada.versao));
        onConcluido(criada.versao);
      } catch (erro) {
        if (isAPIError(erro) && erro.status === 409) {
          setAvisoConflito(erro.message);
          proxima.reload();
          return;
        }
        throw erro;
      }
    },
  });

  const { servicos: textoServicos, materiais: textoMateriais } = form.values;
  const lidoServicos = lerPercentual(textoServicos);
  const lidoMateriais = lerPercentual(textoMateriais);
  const comErro = lidoServicos.erro !== null || lidoMateriais.erro !== null;
  const descarte = useConfirmarDescarte({
    onDescartar: onClose,
    testId: "nova-versao-descarte",
  });

  let estado: NovaVersaoEstado = "pronto";
  if (proxima.status === "forbidden") estado = "proibido";
  else if (proxima.status === "error") estado = "erro";
  else if (proxima.status === "idle" || (proxima.status === "loading" && !origem)) {
    estado = "carregando";
  }

  const etapas: EtapaNovaVersao[] = [
    {
      numero: 1,
      titulo: ETAPA_CORRECAO,
      situacao: etapa === 1 ? "active" : "done",
      ariaLabel: `Etapa 1: ${ETAPA_CORRECAO}`,
      testId: "nova-versao-etapa-1",
      onSelect: etapa === 2 ? () => setEtapa(1) : undefined,
    },
    {
      numero: 2,
      titulo: ETAPA_CONFIRMACAO,
      situacao: etapa === 2 ? "active" : "pending",
      ariaLabel: `Etapa 2: ${ETAPA_CONFIRMACAO}`,
      testId: "nova-versao-etapa-2",
      onSelect: undefined,
    },
  ];

  const correcao = descreverCorrecao(lidoServicos.valor, lidoMateriais.valor);
  const itens = [
    COPIADO_ESCOPOS_EQUIPAMENTO,
    COPIADO_ESCOPOS_CLASSE_OUTROS,
    COPIADO_CUSTOS,
    correcao === null ? COPIADO_SEM_CORRECAO : COPIADO_COM_CORRECAO(correcao),
  ];

  return {
    estado,
    errorMessage: proxima.errorMessage,
    recarregar: proxima.reload,
    etapa,
    etapas,
    origem,
    destino,
    introducao: INTRODUCAO,
    servicos: {
      rotulo: ESCOPOS_PADRAO_ROTULOS.percentualServicos,
      valor: textoServicos,
      erro: lidoServicos.erro,
      digitar: (valor) => form.setValue("servicos", valor),
      testId: "nova-versao-servicos",
    },
    materiais: {
      rotulo: ESCOPOS_PADRAO_ROTULOS.percentualMateriais,
      valor: textoMateriais,
      erro: lidoMateriais.erro,
      digitar: (valor) => form.setValue("materiais", valor),
      testId: "nova-versao-materiais",
    },
    confirmacao: {
      texto: MS11(origem, destino, correcao),
      escoposACopiar: ESCOPOS_A_COPIAR(escoposACopiar),
      itens,
      tituloItens: COPIADO_TITULO(destino),
    },
    proximaDesabilitada: estado !== "pronto" || comErro,
    isSubmitting: form.isSubmitting,
    submitError: avisoConflito ?? form.submitError,
    avancar: () => {
      if (estado === "pronto" && !comErro) setEtapa(2);
    },
    voltar: () => setEtapa(1),
    confirmar: () => {
      if (estado === "pronto") void form.submit();
    },
    cancelar: () => descarte.pedir(percentualPreenchido(textoServicos, textoMateriais)),
    descarte: descarte.confirmacao,
  };
}
